/**
 * Portfolio Summary Card Component
 *
 * Displays a summary of the user's portfolio including:
 * - Total portfolio value (with Indian rupee formatting)
 * - Returns (absolute value and percentage)
 * - Invested amount
 * - XIRR with as-of date
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINR, formatPercent, formatDate } from "@/lib/utils";
import { TrendingUp, TrendingDown, Wallet, PiggyBank, Target } from "lucide-react";
import type { PortfolioSummary } from "@/hooks/useHomeData";

// =============================================================================
// Types
// =============================================================================

export interface PortfolioSummaryCardProps {
  /** Portfolio summary data */
  data: PortfolioSummary;
  /** As-of date for XIRR calculation */
  xirrAsOfDate?: Date | string;
  /** Additional CSS classes */
  className?: string;
}

// =============================================================================
// Sub-components
// =============================================================================

interface StatItemProps {
  label: string;
  value: string;
  subValue?: string;
  icon?: React.ReactNode;
  valueClassName?: string;
}

function StatItem({ label, value, subValue, icon, valueClassName }: StatItemProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className={cn("text-lg font-semibold", valueClassName)}>
        {value}
      </div>
      {subValue && (
        <div className="text-sm text-muted-foreground">{subValue}</div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function PortfolioSummaryCard({
  data,
  xirrAsOfDate = new Date(),
  className,
}: PortfolioSummaryCardProps) {
  const isPositiveReturn = data.totalReturns >= 0;
  const ReturnIcon = isPositiveReturn ? TrendingUp : TrendingDown;

  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">Portfolio Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Total Value - Hero Section */}
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Current Value</p>
          <p className="text-3xl font-bold tracking-tight">
            {formatINR(data.totalValue)}
          </p>
          {/* Daily Change */}
          {(data.dailyChange !== 0 || data.dailyChangePercent !== 0) && (
            <div
              className={cn(
                "flex items-center gap-1 text-sm",
                data.dailyChange >= 0 ? "text-green-600" : "text-red-600"
              )}
            >
              {data.dailyChange >= 0 ? (
                <TrendingUp className="h-3.5 w-3.5" />
              ) : (
                <TrendingDown className="h-3.5 w-3.5" />
              )}
              <span>
                {formatINR(data.dailyChange)} ({formatPercent(data.dailyChangePercent)}) today
              </span>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Invested Amount */}
          <StatItem
            label="Invested"
            value={formatINR(data.totalInvested)}
            icon={<PiggyBank className="h-4 w-4" />}
          />

          {/* Total Returns */}
          <StatItem
            label="Returns"
            value={formatINR(data.totalReturns)}
            subValue={formatPercent(data.totalReturnsPercent)}
            icon={<ReturnIcon className="h-4 w-4" />}
            valueClassName={cn(
              isPositiveReturn ? "text-green-600" : "text-red-600"
            )}
          />

          {/* XIRR */}
          <StatItem
            label="XIRR"
            value={formatPercent(data.xirr)}
            subValue={`as of ${formatDate(xirrAsOfDate)}`}
            icon={<Target className="h-4 w-4" />}
            valueClassName={cn(
              data.xirr >= 0 ? "text-green-600" : "text-red-600"
            )}
          />

          {/* Wealth Created */}
          <StatItem
            label="Wealth Created"
            value={formatINR(data.totalReturns)}
            icon={<Wallet className="h-4 w-4" />}
            valueClassName={cn(
              isPositiveReturn ? "text-green-600" : "text-red-600"
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Skeleton Loader
// =============================================================================

export function PortfolioSummaryCardSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="pb-2">
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Total Value Skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>

        {/* Stats Grid Skeleton */}
        <div className="grid grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-28" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default PortfolioSummaryCard;
